import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, ArrowRight, BookOpen, Shield, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';

const content = {
  de: {
    title: 'Vielen Dank!',
    subtitle: 'Ihr Termin wurde erfolgreich gebucht.',
    confirmText: 'Sie erhalten in Kürze eine Bestätigungs-E-Mail mit allen Details und dem Link zum Online-Meeting.',
    nextSteps: 'Nächste Schritte',
    steps: [
      'Prüfen Sie Ihren Posteingang für die Terminbestätigung',
      'Fügen Sie den Termin Ihrem Kalender hinzu',
      'Bereiten Sie eventuelle Fragen vor'
    ],
    explore: 'Entdecken Sie unsere Lösungen',
    links: [
      { href: '/produktkatalog', icon: BookOpen, label: 'Produktkatalog', desc: 'Alle E-Learning Kurse im Überblick' },
      { href: '/visitor-system', icon: Shield, label: 'Besucherunterweisung', desc: 'Digitale Sicherheitsunterweisungen' },
      { href: '/instruction-system', icon: Monitor, label: 'Unterweisungssystem', desc: 'Komplettes Unterweisungsmanagement' }
    ],
    backHome: 'Zur Startseite'
  },
  en: {
    title: 'Thank You!',
    subtitle: 'Your appointment has been successfully booked.',
    confirmText: 'You will shortly receive a confirmation email with all details and the link to the online meeting.',
    nextSteps: 'Next Steps',
    steps: [
      'Check your inbox for the appointment confirmation',
      'Add the appointment to your calendar',
      'Prepare any questions you may have'
    ],
    explore: 'Explore Our Solutions',
    links: [
      { href: '/produktkatalog', icon: BookOpen, label: 'Product Catalog', desc: 'All e-learning courses at a glance' },
      { href: '/visitor-system', icon: Shield, label: 'Visitor Instruction', desc: 'Digital safety instructions' },
      { href: '/instruction-system', icon: Monitor, label: 'Instruction System', desc: 'Complete instruction management' }
    ],
    backHome: 'Back to Home'
  }
};

export default function BookingConfirmationPage() {
  const { language } = useLanguage();
  const t = content[language] || content.de;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="booking-confirmation-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Success Icon & Message */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0D2440] mb-4">{t.title}</h1>
          <p className="text-xl text-[#2E5E99] font-medium mb-3">{t.subtitle}</p>
          <p className="text-[#5A7A9B] max-w-lg mx-auto">{t.confirmText}</p>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-lg font-semibold text-[#0D2440] mb-5">{t.nextSteps}</h2>
          <div className="space-y-4">
            {t.steps.map((step, i) => (
              <div key={`step-${step.slice(0, 20)}`} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2E5E99] text-white text-sm font-semibold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <p className="text-[#0D2440]">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Explore Solutions */}
        <h2 className="text-lg font-semibold text-[#0D2440] mb-5">{t.explore}</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {t.links.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="group bg-white rounded-xl p-5 border border-gray-200 hover:border-[#2E5E99] hover:shadow-md transition-all"
              data-testid={`confirmation-link-${link.href}`}
            >
              <link.icon className="w-7 h-7 text-[#2E5E99] mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-[#0D2440] text-sm mb-1">{link.label}</h3>
              <p className="text-xs text-[#5A7A9B]">{link.desc}</p>
            </Link>
          ))}
        </div>

        {/* Back Home */}
        <div className="text-center">
          <Link to="/">
            <Button variant="outline" className="border-[#0D2440] text-[#0D2440] hover:bg-[#0D2440] hover:text-white" data-testid="back-home-btn">
              {t.backHome}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
